import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { 
  Users, Route, Star, BookOpen, FileCheck, Send, 
  CheckCircle, Clock, XCircle, ChevronDown, ChevronUp 
} from 'lucide-react';

const StudentStats = ({ userId, token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLessons, setShowLessons] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);

  useEffect(() => {
    if (!userId || !token) return;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        };
        const res = await axios.get(`/api/users/${userId}/stats`, config);
        setStats(res.data);
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to load student stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId, token]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const { group, pathway, xp, pathProgress, completedCourses, completedLessons, completedSubmissionForms, submissions } = stats;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={14} className="text-green-500" />;
      case 'pending': return <Clock size={14} className="text-yellow-500" />;
      case 'rejected': return <XCircle size={14} className="text-red-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <h2 className="text-xl font-bold text-[#054581] mb-6">Student Overview</h2>

      {/* Info Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Group */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Users size={16} />
            <span>Group</span>
          </div>
          <div className="font-semibold text-[#1e2d40]">
            {group?.title || 'No group assigned'}
          </div>
          {group?.program && (
            <div className="text-xs text-gray-500 mt-1">Program: {group.program}</div>
          )}
        </div>

        {/* Pathway */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Route size={16} />
            <span>Pathway</span>
          </div>
          <div className="font-semibold text-[#1e2d40]">
            {pathway?.title || 'No pathway assigned'}
          </div>
        </div>

        {/* XP Points */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Star size={16} />
            <span>XP Points</span>
          </div>
          <div className="font-semibold text-[#1e2d40]">
            {xp?.totalXP?.toLocaleString() || 0} XP
          </div>
          <div className="text-xs text-gray-500 mt-1">Level {xp?.level || 1}</div>
        </div>

        {/* Completed Courses */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <BookOpen size={16} />
            <span>Courses Completed</span>
          </div>
          <div className="font-semibold text-[#1e2d40]">
            {completedCourses?.length || 0}
          </div>
        </div>
      </div>

      {/* Pathway Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#1e2d40]">Pathway Progress</h3>
          <span className="text-sm font-bold text-[#1086a4]">
            {pathProgress?.percentageComplete || 0}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-500"
            style={{
              width: `${pathProgress?.percentageComplete || 0}%`,
              backgroundColor: '#1086a4'
            }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{pathProgress?.contentCompleted || 0} / {pathProgress?.totalContent || 0} activities completed</span>
          <span>{pathProgress?.lessonsCompleted || 0} lessons, {pathProgress?.submissionFormsCompleted || 0} submissions</span>
        </div>
      </div>

      {/* Completed Courses List */}
      {completedCourses && completedCourses.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-[#1e2d40] mb-2 flex items-center gap-2">
            <BookOpen size={16} className="text-[#1086a4]" />
            Completed Courses
          </h3>
          <div className="flex flex-wrap gap-2">
            {completedCourses.map(course => (
              <span key={course._id} className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm border border-green-200">
                <CheckCircle size={14} />
                {course.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Completed Lessons (collapsible) */}
      {completedLessons && completedLessons.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowLessons(!showLessons)}
            className="flex items-center gap-2 text-sm font-semibold text-[#1e2d40] mb-2 hover:text-[#1086a4] transition-colors"
          >
            <FileCheck size={16} className="text-[#1086a4]" />
            Completed Lessons ({completedLessons.length})
            {showLessons ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showLessons && (
            <div className="bg-gray-50 rounded-lg border border-gray-100 divide-y divide-gray-100">
              {completedLessons.map((lesson, i) => (
                <div key={lesson._id || i} className="px-4 py-2 flex items-center justify-between">
                  <span className="text-sm text-[#1e2d40]">{lesson.title}</span>
                  <span className="text-xs text-gray-500">
                    {lesson.completedAt ? new Date(lesson.completedAt).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Submissions (collapsible) */}
      {submissions && submissions.length > 0 && (
        <div>
          <button
            onClick={() => setShowSubmissions(!showSubmissions)}
            className="flex items-center gap-2 text-sm font-semibold text-[#1e2d40] mb-2 hover:text-[#1086a4] transition-colors"
          >
            <Send size={16} className="text-[#1086a4]" />
            Submissions ({submissions.length})
            {showSubmissions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showSubmissions && (
            <div className="bg-gray-50 rounded-lg border border-gray-100 divide-y divide-gray-100">
              {submissions.map((sub, i) => (
                <div key={sub._id || i} className="px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#1e2d40]">{sub.formTitle}</span>
                    {getStatusBadge(sub.status)}
                  </div>
                  <span className="text-xs text-gray-500">
                    {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {(!completedCourses || completedCourses.length === 0) && 
       (!completedLessons || completedLessons.length === 0) && 
       (!submissions || submissions.length === 0) && (
        <div className="text-center py-4 text-gray-500 text-sm">
          No completed activities yet.
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.token
});

export default connect(mapStateToProps)(StudentStats);
